<template>
    <v-text-field
        ref="field"
        v-bind="$attrs"
        :value="value"
        :rules="allRules"
        :messages="suggestionMessages"
        :required="required"
        autocomplete="email"
        inputmode="email"
        class="email-field"
        v-on="fieldListeners"
    >
        <!-- Hinweis und Fehlermeldung laufen beide über den regulären Meldungs-
             bereich, damit Vuetify den Platz dafür reserviert und nichts überlappt.
             Steckt eine vorgeschlagene Adresse darin, wird sie anklickbar. -->
        <template #message="{ message }">
            <span v-if="message === SUGGESTION_MARKER" class="email-field__suggestion">
                Meinten Sie
                <button type="button" class="email-field__apply" @click="applySuggestion">{{ suggestion }}</button
                >?
            </span>
            <span v-else-if="typoVorschlag(message)">
                {{ TYPO_PREFIX
                }}<button type="button" class="email-field__apply" @click="apply(typoVorschlag(message))">
                    {{ typoVorschlag(message) }}</button
                >?
            </span>
            <span v-else>{{ message }}</span>
        </template>

        <!-- Einfache Slots (etwa label) über das klassische slot-Attribut weiterreichen:
             Vuetify liest sie aus $slots, ein scoped Slot käme dort nicht an. -->
        <template v-for="(_, name) in $slots" :slot="name">
            <slot :name="name" />
        </template>

        <template v-for="(_, name) in passThroughSlots" #[name]="scope">
            <slot :name="name" v-bind="scope" />
        </template>
    </v-text-field>
</template>

<script>
/**
 * E-Mail-Feld mit Prüfung und Tippfehler-Hinweis.
 *
 * Ersetzt das blanke v-text-field in allen Kundenformularen. Es prüft das Format
 * (siehe utils/emailCheck.js), entfernt beim Verlassen des Feldes Leerzeichen am
 * Rand und bietet bei einem bekannten Vertipper die richtige Adresse per Klick an
 * ("Meinten Sie max@t-online.de?").
 *
 * Alle weiteren Attribute (label, outlined, dense, error-messages …) werden an das
 * v-text-field durchgereicht, ebenso alle Slots.
 */
import { emailRules, emailRulesOptional, isValidEmail, normalizeEmail, suggestKnownTypo, suggestSimilarDomain, TYPO_PREFIX } from "@/utils/emailCheck"

export default {
    name: "EmailField",
    inheritAttrs: false,
    props: {
        // Bewusst ohne `type: String`: mehrere Formulare legen das Feld mit
        // `email: null` an, und Vuetifys reset() setzt den Wert ebenfalls auf null.
        // Mit einer String-Typangabe warnt Vue dann bei jedem Mounten und nach
        // jedem Zuruecksetzen in der Konsole.
        value: {
            default: "",
        },
        /** false = leeres Feld ist erlaubt (optionale Angabe). */
        required: {
            type: Boolean,
            default: true,
        },
        /** Zusätzliche Regeln des jeweiligen Formulars. */
        rules: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            touched: false,
            // Erkennungsmarke, damit der Meldungs-Slot den weichen Hinweis von einer
            // echten Fehlermeldung unterscheiden kann.
            SUGGESTION_MARKER: "__email-vorschlag__",
            TYPO_PREFIX,
        }
    },
    computed: {
        allRules() {
            return [...(this.required ? emailRules : emailRulesOptional), ...this.rules]
        },
        // Nur der weiche Fall: ein Zeichen Abstand zu einer bekannten Domain.
        // Belegte Vertipper werden von der Regel abgewiesen und erscheinen als
        // Fehlermeldung, nicht als Hinweis.
        //
        // Zwei Fälle bleiben draußen. Erstens: ist die Domain ein belegter
        // Vertipper, steht die Korrektur schon in der Fehlermeldung — der weiche
        // Hinweis würde sie nur verdecken, weil Vuetify `messages` VOR dem
        // errorBucket anzeigt. Zweitens: nur vorschlagen, was selbst gültig ist.
        // Aus "max mustermann@gmx.d" käme sonst "max mustermann@gmx.de", und ein
        // Klick darauf ließe das Feld genauso rot stehen wie vorher.
        suggestion() {
            if (suggestKnownTypo(this.value)) return null
            const vorschlag = suggestSimilarDomain(this.value)
            return vorschlag && isValidEmail(vorschlag) ? vorschlag : null
        },
        // Erst anzeigen, wenn das Feld einmal verlassen wurde — sonst blinkt der
        // Hinweis schon während des Tippens auf.
        showSuggestion() {
            return this.touched && !!this.suggestion
        },
        suggestionMessages() {
            return this.showSuggestion ? [this.SUGGESTION_MARKER] : []
        },
        // Zustand des inneren Feldes nach außen spiegeln, damit Formulare, die ihre
        // Felder per ref abfragen, unverändert weiterarbeiten.
        hasError() {
            return this.$refs.field ? this.$refs.field.hasError : false
        },
        valid() {
            return this.$refs.field ? this.$refs.field.valid : true
        },
        // Scoped Slots durchreichen — ohne den selbst belegten Meldungs-Slot und
        // ohne die einfachen Slots, die oben schon weitergereicht werden.
        passThroughSlots() {
            const slots = { ...this.$scopedSlots }
            delete slots.message
            Object.keys(this.$slots).forEach((name) => delete slots[name])
            return slots
        },
        fieldListeners() {
            return {
                ...this.$listeners,
                input: (val) => this.$emit("input", val),
                blur: (event) => {
                    this.touched = true
                    const cleaned = normalizeEmail(this.value)
                    // Nicht nur nach aussen melden, sondern in das innere Feld
                    // schreiben — aus demselben Grund wie bei apply(): Formulare, die
                    // an @input haengen und sofort validate() rufen, saehen sonst noch
                    // den ungetrimmten Wert und liessen den Weiter-Button gesperrt.
                    if (cleaned !== this.value) this.schreibeWert(cleaned)
                    this.$emit("blur", event)
                },
            }
        },
    },
    methods: {
        // Formulare greifen teils per ref direkt auf ihre Felder zu (etwa die
        // Buchungsstrecke, die je Schritt validate() aufruft). Deshalb verhält sich
        // diese Komponente nach außen wie ein v-text-field.
        validate(force) {
            return this.$refs.field ? this.$refs.field.validate(force) : true
        },
        reset() {
            // Auch den Hinweis-Zustand zuruecksetzen, sonst erscheint der weiche
            // Vorschlag beim naechsten Ausfuellen schon waehrend des Tippens.
            this.touched = false
            if (this.$refs.field) this.$refs.field.reset()
        },
        resetValidation() {
            if (this.$refs.field) this.$refs.field.resetValidation()
        },
        focus() {
            if (this.$refs.field) this.$refs.field.focus()
        },
        blur() {
            if (this.$refs.field) this.$refs.field.blur()
        },
        applySuggestion() {
            this.apply(this.suggestion)
        },
        apply(adresse) {
            if (!adresse) return
            this.schreibeWert(adresse)
        },
        // Den Wert in das innere Feld schreiben, nicht nur nach aussen melden.
        // Sonst steht dort beim naechsten synchronen validate() noch die alte,
        // vertippte Adresse: Formulare wie die Buchungsstrecke haengen an
        // @input und pruefen sofort — der Weiter-Button bliebe gesperrt,
        // obwohl die Korrektur schon im Feld steht. Der Setter von
        // internalValue loest das input-Ereignis selbst aus, das der Listener
        // nach aussen weiterreicht.
        schreibeWert(wert) {
            const feld = this.$refs.field
            if (feld) feld.internalValue = wert
            else this.$emit("input", wert)
        },
        /** Zieht die vorgeschlagene Adresse aus einer Fehlermeldung heraus. */
        typoVorschlag(message) {
            if (typeof message !== "string" || !message.startsWith(TYPO_PREFIX)) return null
            return message.slice(TYPO_PREFIX.length).replace(/\?$/, "")
        },
    },
}
</script>

<style scoped>
.email-field__suggestion {
    line-height: 1.4;
}

.email-field__apply {
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    font-weight: 600;
    color: var(--v-primary-base, #1976d2);
    text-decoration: underline;
    cursor: pointer;
}

.email-field__apply:hover,
.email-field__apply:focus {
    text-decoration: none;
}
</style>
